import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { del, get, post, put } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import { toast } from "../../Utils/Helper/toast";

/** Izba Admin → Config → Account Plans: briefing questions and sections, per tenant. */

export type SectionOwner = "AE" | "CSM";

export interface TemplateQuestion {
  key: string;
  text: string;
  enabled: boolean;
  stated_phrases: string[];
  custom: boolean;
  /** Sections a missing answer shows as a gap in (absent on templates saved before 2026-10-22). */
  informs?: string[];
}

export interface TemplateSection {
  key: string;
  title: string;
  enabled: boolean;
  system: boolean;
  custom: boolean;
  owner: SectionOwner | null;
  collapsed_for_csm: boolean;
  max_items: number | null;
  about: string;
  instructions: string | null;
}

export interface PlanTemplate {
  questions: TemplateQuestion[];
  sections: TemplateSection[];
}

export interface TemplateVersion {
  id: string;
  version_no: number;
  action: "publish" | "rollback" | "follow_default";
  note: string | null;
  follows_default: boolean;
  template_hash: string | null;
  eval_id: string | null;
  forced: boolean;
  force_reason: string | null;
  by: string;
  at: string | null;
}

export interface TemplateTenant {
  schema: string;
  name: string;
  is_active: boolean;
  follows_default: boolean;
  live_version_no: number | null;
  customised: number;
  has_draft: boolean;
}

export interface TemplateOverview {
  default: {
    source: "code" | "default";
    version: TemplateVersion | null;
    has_draft: boolean;
  };
  code_template: PlanTemplate;
  tenants: TemplateTenant[];
  owners: SectionOwner[];
  eval_gate: string;
}

export interface TemplateScopeDetail {
  scope: string;
  name: string | null;
  live: {
    source: "code" | "default" | "tenant";
    template: PlanTemplate;
    version: TemplateVersion | null;
    follows_default: boolean;
  };
  default_template: PlanTemplate;
  draft: {
    template: PlanTemplate;
    base_version_id: string | null;
    by: string;
    at: string | null;
  } | null;
  history: TemplateVersion[];
}

export const DEFAULT_SCOPE = "default";

export type TemplateBusy =
  | "save"
  | "discard"
  | "publish"
  | "rollback"
  | "follow"
  | null;

interface AccountPlanTemplateState {
  overview: TemplateOverview | null;
  loading: boolean;
  loadError: string | null;
  scope: string;
  details: Record<string, TemplateScopeDetail>;
  scopeLoading: boolean;
  // Unsaved edits per scope, on top of the saved draft (or live template).
  working: Record<string, PlanTemplate>;
  busy: TemplateBusy;
  actionError: string | null;
}

const initialState: AccountPlanTemplateState = {
  overview: null,
  loading: false,
  loadError: null,
  scope: DEFAULT_SCOPE,
  details: {},
  scopeLoading: false,
  working: {},
  busy: null,
  actionError: null,
};

const errorMessage = (error: unknown, fallback: string): string => {
  const anyError = error as {
    response?: { data?: { detail?: unknown; message?: unknown } };
  };
  const detail =
    anyError?.response?.data?.detail || anyError?.response?.data?.message;
  return typeof detail === "string" && detail.trim() ? detail : fallback;
};

const isOverview = (value: unknown): value is TemplateOverview =>
  !!value &&
  typeof value === "object" &&
  Array.isArray((value as TemplateOverview).tenants) &&
  !!(value as TemplateOverview).code_template;

const isDetail = (value: unknown): value is TemplateScopeDetail =>
  !!value &&
  typeof value === "object" &&
  typeof (value as TemplateScopeDetail).scope === "string" &&
  !!(value as TemplateScopeDetail).live?.template;

/** What the editor starts from for a scope: the saved draft, else what is live. */
export const savedTemplate = (detail: TemplateScopeDetail): PlanTemplate =>
  detail.draft?.template ?? detail.live.template;

/** The version a save must name: the draft's base, else the live version. */
const baseVersionId = (detail: TemplateScopeDetail): string | null =>
  detail.draft
    ? detail.draft.base_version_id
    : (detail.live.version?.id ?? null);

type RootLike = { accountPlanTemplate: AccountPlanTemplateState };

const detailOrThrow = (content: unknown): TemplateScopeDetail => {
  if (!isDetail(content)) throw new Error("Invalid template response");
  return content;
};

export const fetchAccountPlanTemplate = createAsyncThunk<
  TemplateOverview,
  void,
  { rejectValue: string }
>("accountPlanTemplate/fetch", async (_, { rejectWithValue }) => {
  try {
    const response = await get(API_URLS.accountPlansTemplate());
    if (!isOverview(response?.content)) {
      return rejectWithValue("Invalid Account Plans template response");
    }
    return response.content;
  } catch (error: unknown) {
    return rejectWithValue(
      errorMessage(error, "Failed to load the Account Plans template.")
    );
  }
});

export const fetchTemplateScope = createAsyncThunk<
  TemplateScopeDetail,
  string,
  { rejectValue: string }
>("accountPlanTemplate/fetchScope", async (scope, { rejectWithValue }) => {
  try {
    const response = await get(API_URLS.accountPlansTemplateScope(scope));
    return detailOrThrow(response?.content);
  } catch (error: unknown) {
    return rejectWithValue(
      errorMessage(error, "Failed to load this template.")
    );
  }
});

/** Saves the scope's unsaved edits as its draft. */
export const saveTemplateDraft = createAsyncThunk<
  TemplateScopeDetail,
  string,
  { rejectValue: string; state: RootLike }
>(
  "accountPlanTemplate/saveDraft",
  async (scope, { getState, rejectWithValue }) => {
    const { details, working } = getState().accountPlanTemplate;
    const detail = details[scope];
    if (!detail) return rejectWithValue("This template hasn't loaded yet.");
    try {
      const response = await put(
        API_URLS.accountPlansTemplateScope(scope, "draft"),
        {
          template: working[scope] ?? savedTemplate(detail),
          base_version_id: baseVersionId(detail),
        }
      );
      const out = detailOrThrow(response?.content);
      toast.success("Draft saved");
      return out;
    } catch (error: unknown) {
      return rejectWithValue(errorMessage(error, "Couldn't save the draft."));
    }
  }
);

export const discardTemplateDraftOnServer = createAsyncThunk<
  TemplateScopeDetail,
  string,
  { rejectValue: string }
>("accountPlanTemplate/discardDraft", async (scope, { rejectWithValue }) => {
  try {
    const response = await del(
      API_URLS.accountPlansTemplateScope(scope, "draft")
    );
    const out = detailOrThrow(response?.content);
    toast.success("Draft discarded");
    return out;
  } catch (error: unknown) {
    return rejectWithValue(errorMessage(error, "Couldn't discard the draft."));
  }
});

export interface PublishArgs {
  scope: string;
  note: string;
  // The signed admin-tools eval of this template, as pasted.
  attestation?: Record<string, unknown> | null;
  // Emergency only: publish without an eval, with a reason kept on the version.
  forceReason?: string;
}

/** Saves any unsaved edits, then publishes the draft (eval-gated on the server). */
export const publishTemplate = createAsyncThunk<
  TemplateScopeDetail,
  PublishArgs,
  { rejectValue: string; state: RootLike }
>(
  "accountPlanTemplate/publish",
  async (
    { scope, note, attestation, forceReason },
    { getState, rejectWithValue }
  ) => {
    const { details, working } = getState().accountPlanTemplate;
    const detail = details[scope];
    if (!detail) return rejectWithValue("This template hasn't loaded yet.");
    try {
      if (working[scope]) {
        await put(API_URLS.accountPlansTemplateScope(scope, "draft"), {
          template: working[scope],
          base_version_id: baseVersionId(detail),
        });
      }
      const response = await post(
        API_URLS.accountPlansTemplateScope(scope, "publish"),
        {
          note: note.trim() || null,
          attestation: attestation ?? null,
          force: !!forceReason,
          force_reason: forceReason?.trim() || null,
        }
      );
      const out = detailOrThrow(response?.content);
      toast.success(`Published v${out.live.version?.version_no ?? ""}`);
      return out;
    } catch (error: unknown) {
      return rejectWithValue(errorMessage(error, "Couldn't publish."));
    }
  }
);

export const rollbackTemplate = createAsyncThunk<
  TemplateScopeDetail,
  { scope: string; versionId: string },
  { rejectValue: string }
>(
  "accountPlanTemplate/rollback",
  async ({ scope, versionId }, { rejectWithValue }) => {
    try {
      const response = await post(
        API_URLS.accountPlansTemplateScope(scope, "rollback"),
        { version_id: versionId }
      );
      const out = detailOrThrow(response?.content);
      toast.success("Rolled back");
      return out;
    } catch (error: unknown) {
      return rejectWithValue(errorMessage(error, "Couldn't roll back."));
    }
  }
);

export const followDefaultTemplate = createAsyncThunk<
  TemplateScopeDetail,
  string,
  { rejectValue: string }
>("accountPlanTemplate/followDefault", async (scope, { rejectWithValue }) => {
  try {
    const response = await post(
      API_URLS.accountPlansTemplateScope(scope, "follow-default"),
      {}
    );
    const out = detailOrThrow(response?.content);
    toast.success("Now follows the default");
    return out;
  } catch (error: unknown) {
    return rejectWithValue(
      errorMessage(error, "Couldn't switch back to the default.")
    );
  }
});

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const workingFor = (
  state: AccountPlanTemplateState,
  scope: string
): PlanTemplate | null => {
  const detail = state.details[scope];
  if (!detail) return null;
  if (!state.working[scope])
    state.working[scope] = clone(savedTemplate(detail));
  return state.working[scope];
};

type Kind = "questions" | "sections";

interface ItemRef {
  kind: Kind;
  key: string;
}

/**
 * Whether a section can move `by` places. The account header stays first, custom
 * sections stay after the standard ones, and only the default template reorders the
 * standard sections (a tenant's follow the default's order).
 */
export const canMoveSection = (
  sections: TemplateSection[],
  key: string,
  by: number,
  isDefaultScope: boolean
): boolean => {
  const i = sections.findIndex(x => x.key === key);
  const j = i + by;
  if (i < 1 || j < 1 || j >= sections.length) return false;
  const custom = sections[i].custom;
  if (!custom && !isDefaultScope) return false;
  return sections[j].custom === custom;
};

const move = <T>(items: T[], from: number, to: number): void => {
  if (to < 0 || to >= items.length) return;
  const [item] = items.splice(from, 1);
  items.splice(to, 0, item);
};

const WRITES: [
  { pending: any; fulfilled: any; rejected: any },
  TemplateBusy,
][] = [
  [saveTemplateDraft, "save"],
  [discardTemplateDraftOnServer, "discard"],
  [publishTemplate, "publish"],
  [rollbackTemplate, "rollback"],
  [followDefaultTemplate, "follow"],
];

const accountPlanTemplateSlice = createSlice({
  name: "accountPlanTemplate",
  initialState,
  reducers: {
    setTemplateScope(state, action: PayloadAction<string>) {
      state.scope = action.payload;
      state.actionError = null;
    },
    updateTemplateQuestion(
      state,
      action: PayloadAction<{ key: string; patch: Partial<TemplateQuestion> }>
    ) {
      const t = workingFor(state, state.scope);
      const q = t?.questions.find(x => x.key === action.payload.key);
      if (q) Object.assign(q, action.payload.patch);
    },
    updateTemplateSection(
      state,
      action: PayloadAction<{ key: string; patch: Partial<TemplateSection> }>
    ) {
      const t = workingFor(state, state.scope);
      const s = t?.sections.find(x => x.key === action.payload.key);
      if (s) Object.assign(s, action.payload.patch);
    },
    moveTemplateItem(state, action: PayloadAction<ItemRef & { by: number }>) {
      const t = workingFor(state, state.scope);
      if (!t) return;
      const { kind, key, by } = action.payload;
      const items = t[kind] as { key: string }[];
      const i = items.findIndex(x => x.key === key);
      if (i < 0) return;
      if (
        kind === "sections" &&
        !canMoveSection(t.sections, key, by, state.scope === DEFAULT_SCOPE)
      )
        return;
      move(items, i, i + by);
    },
    addTemplateQuestion(state, action: PayloadAction<{ key: string }>) {
      workingFor(state, state.scope)?.questions.push({
        key: action.payload.key,
        text: "",
        enabled: true,
        stated_phrases: [],
        custom: true,
        informs: [],
      });
    },
    addTemplateSection(state, action: PayloadAction<{ key: string }>) {
      workingFor(state, state.scope)?.sections.push({
        key: action.payload.key,
        title: "",
        enabled: true,
        system: false,
        custom: true,
        owner: "AE",
        collapsed_for_csm: false,
        max_items: 8,
        about: "",
        instructions: "",
      });
    },
    removeTemplateItem(state, action: PayloadAction<ItemRef>) {
      const t = workingFor(state, state.scope);
      if (!t) return;
      const items = t[action.payload.kind] as { key: string }[];
      const i = items.findIndex(x => x.key === action.payload.key);
      if (i >= 0) items.splice(i, 1);
      // A removed section is no longer informed by any question.
      if (action.payload.kind === "sections")
        t.questions.forEach(q => {
          if (q.informs)
            q.informs = q.informs.filter(k => k !== action.payload.key);
        });
    },
    resetTemplateItem(state, action: PayloadAction<ItemRef>) {
      const t = workingFor(state, state.scope);
      const detail = state.details[state.scope];
      if (!t || !detail) return;
      const { kind, key } = action.payload;
      const base = (detail.default_template[kind] as { key: string }[]).find(
        x => x.key === key
      );
      const items = t[kind] as { key: string }[];
      const i = items.findIndex(x => x.key === key);
      if (base && i >= 0) items[i] = clone(base);
    },
    /** Drops unsaved edits; the saved draft (if any) stays. */
    revertTemplateEdits(state) {
      delete state.working[state.scope];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAccountPlanTemplate.pending, state => {
        state.loading = true;
        state.loadError = null;
      })
      .addCase(fetchAccountPlanTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.overview = action.payload;
      })
      .addCase(fetchAccountPlanTemplate.rejected, (state, action) => {
        state.loading = false;
        state.loadError =
          action.payload || "Failed to load the Account Plans template.";
      })
      .addCase(fetchTemplateScope.pending, state => {
        state.scopeLoading = true;
      })
      .addCase(fetchTemplateScope.fulfilled, (state, action) => {
        state.scopeLoading = false;
        state.details[action.payload.scope] = action.payload;
      })
      .addCase(fetchTemplateScope.rejected, (state, action) => {
        state.scopeLoading = false;
        state.actionError = action.payload || "Failed to load this template.";
      });
    WRITES.forEach(([thunk, busy]) => {
      builder
        .addCase(thunk.pending, state => {
          state.busy = busy;
          state.actionError = null;
        })
        .addCase(
          thunk.fulfilled,
          (state, action: PayloadAction<TemplateScopeDetail>) => {
            state.busy = null;
            state.details[action.payload.scope] = action.payload;
            delete state.working[action.payload.scope];
          }
        )
        .addCase(
          thunk.rejected,
          (state, action: PayloadAction<string | undefined>) => {
            state.busy = null;
            state.actionError = action.payload || "Something went wrong.";
          }
        );
    });
  },
});

export const {
  setTemplateScope,
  updateTemplateQuestion,
  updateTemplateSection,
  moveTemplateItem,
  addTemplateQuestion,
  addTemplateSection,
  removeTemplateItem,
  resetTemplateItem,
  revertTemplateEdits,
} = accountPlanTemplateSlice.actions;

export default accountPlanTemplateSlice.reducer;
